import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Mail, MessageCircle } from "lucide-react";
import { api } from "../../../booking/api";

const Dot = ({ ok }) => <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />;

const TemplateGuide = ({ g, approved, copy }) => (
  <div className="border rounded p-3 space-y-2" data-testid={`template-guide-${g.name}`}>
    <p className="flex items-center gap-2 font-medium text-xs">
      <Dot ok={approved} /> <code>{g.name}</code> · {g.purpose} · {approved ? "approved ✓" : "waiting for approval"}
    </p>
    <p className="text-xs text-muted-foreground">Category <b>{g.category}</b> · Header: {g.header} · Buttons: {g.buttons}</p>
    {Object.entries(g.texts).map(([lang, text]) => (
      <div key={lang} className="flex gap-2 items-start bg-stone-50 border rounded p-2">
        <span className="text-xs font-mono uppercase w-6 shrink-0 mt-0.5">{lang}</span>
        <p className="text-xs flex-1">{text}</p>
        <button onClick={() => copy(text)} aria-label={`Copy ${g.name} ${lang}`} data-testid={`copy-${g.name}-${lang}`}
          className="text-stone-400 hover:text-stone-900"><Copy size={14} /></button>
      </div>
    ))}
  </div>
);

export const IntegrationsPanel = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/api/admin/integrations").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const w = d.whatsapp;
  const ok = (n) => w.approved?.includes(n);
  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Template copied"));
  const flows = [
    ["Morning arrival (08:00)", ok(w.template_name) ? w.template_name : `${w.fallback_template} (fallback, lounges only)`, true],
    ["Booking confirmation", ok(w.confirm_template) ? w.confirm_template : `${w.confirm_fallback} (fallback)`, ok(w.confirm_template) || ok(w.confirm_fallback)],
    ["Thank-you on Depart", w.review_template, ok(w.review_template)],
    ["Midday massage offer (12:00)", w.massage_template, ok(w.massage_template)],
  ];
  return (
    <div className="bg-white rounded-lg border p-6 space-y-5 mt-6" data-testid="integrations-panel">
      <h2 className="font-semibold">Notifications</h2>
      <div className="text-sm space-y-1" data-testid="integrations-email">
        <p className="flex items-center gap-2 font-medium"><Mail size={15} /> Email <Dot ok={d.email.provider === "resend"} /></p>
        <p className="text-muted-foreground">
          Sending from <b>{d.email.from}</b> via {d.email.provider === "resend" ? "your Resend account" : "Emergent managed email"} ·
          {" "}{d.email.queued} queued · {d.email.failed} failed{d.email.last_error ? ` · last error: ${d.email.last_error}` : ""}
        </p>
      </div>
      <div className="text-sm space-y-3" data-testid="integrations-whatsapp">
        <p className="flex items-center gap-2 font-medium"><MessageCircle size={15} /> WhatsApp (respond.io) <Dot ok={w.configured} /></p>
        <p className="text-muted-foreground">{w.configured ? `Connected · sent ${w.sent} · failed ${w.failed}` : "Not connected yet."}</p>
        <ul className="text-xs space-y-1" data-testid="integrations-whatsapp-flows">
          {flows.map(([label, tpl, active]) => (
            <li key={label} className="flex items-center gap-2"><Dot ok={active} /> {label}: <code>{tpl}</code> {active ? "· active" : "· off until approved"}</li>
          ))}
        </ul>
        <p className="text-muted-foreground text-xs">Create these in respond.io → Settings → Channels → WhatsApp → Templates. The text must match exactly; they switch on automatically once Meta approves.</p>
        {w.guide.map((g) => <TemplateGuide key={g.name} g={g} approved={ok(g.name)} copy={copy} />)}
      </div>
    </div>
  );
};
